import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatCurrency } from '@/lib/utils'
import { type AccidentViolationItem } from '@/models/dashboard'
import { ClaimStatusBadge } from './ClaimStatusBadge'
import { DetailRow } from './DetailRow'
import { formatEventDate } from './violationDisplay'

function longDate(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function amount(value: number | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined
  return formatCurrency(value)
}

function policyPeriod(item: AccidentViolationItem): string | undefined {
  const start = item.policyPeriodStart ? formatEventDate(item.policyPeriodStart) : undefined
  const end = item.policyPeriodEnd ? formatEventDate(item.policyPeriodEnd) : undefined
  if (start && end) return `${start} – ${end}`
  return start || end
}

/** Everything the loss run told us about one claim. */
export function ClaimDetailCards({ item }: { item: AccidentViolationItem }) {
  const hasAmounts =
    item.paidLoss != null ||
    item.reserve != null ||
    item.allocatedExpense != null ||
    item.totalIncurred != null

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Claim</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="Incident date" value={longDate(item.date)} />
          <DetailRow label="Claim number" value={item.claimNumber} />
          <DetailRow label="Cai reference" value={item.claimReference} />
          <DetailRow label="Coverage" value={item.coverageLabel} />
          <DetailRow label="Status" value={<ClaimStatusBadge status={item.claimStatus} />} />
          {(item.claimCount ?? 1) > 1 && (
            <DetailRow label="Claims in this entry" value={String(item.claimCount)} />
          )}
          <DetailRow label="Driver" value={item.driverName} />
        </CardContent>
      </Card>

      {hasAmounts && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Amounts</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow label="Paid loss" value={amount(item.paidLoss)} />
            <DetailRow label="Reserve" value={amount(item.reserve)} />
            <DetailRow label="Allocated expense" value={amount(item.allocatedExpense)} />
            <DetailRow label="Total incurred" value={amount(item.totalIncurred)} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Loss run</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="Insurer" value={item.insurer} />
          <DetailRow label="Policy number" value={item.policyNumber} />
          <DetailRow label="Policy period" value={policyPeriod(item)} />
          <DetailRow
            label="Requested on"
            value={item.reportedOn ? formatEventDate(item.reportedOn) : undefined}
          />
        </CardContent>
      </Card>
    </div>
  )
}
